import isNil from 'lodash/fp/isNil';

import { hasValidationErrors, type ParameterDescription } from './RiskMetricsParameterService.tsx';
import {
  type IMetricCalculatorParameterInput,
  type IRiskDashboardInputQuery,
  IParameterType,
  type IPortfolioMetricsQuery,
  type IPortfolioMetricsQueryVariables,
  type IPortfolioRiskMetricsInput,
} from '../../../generated/graphql';
import { type AssetLabelInput, isAssetLabelInput } from '../../market/asset/AssetLabelService.ts';
import type { AvailableContributionMetrics } from './AvailableContributionMetrics.ts';
import dayjs from 'dayjs';
import { convertDateInUtcToUTCISODate } from 'components/date.utils.ts';
import uniq from 'lodash/fp/uniq';
import type { MetricInput } from './RiskMetricsChart.types.ts';
import type { MetricsData } from './RiskMetricsChart.tsx';

const validateEmptyValues = (unknownValues: unknown[] | null | undefined, name: string): unknownValues is unknown[] => {
  if (isNil(unknownValues)) {
    console.error('Missing values for param', name);
    return false;
  }

  if (unknownValues.length === 0) {
    console.error('Empty values for param', name);
    return false;
  }

  return true;
};

const calculateParameterDefaultValue = (param: ParameterDescription): string | string[] | AssetLabelInput[] => {
  if (param.type === IParameterType.Int) {
    if (!param.minMax) {
      console.error('Missing minMaxParam value for metric', param.name);
      return '';
    }

    return param.minMax.default.toString();
  }

  if (param.type === IParameterType.Options) {
    if (!validateEmptyValues(param.options, param.name)) {
      return '';
    }

    return [param.options[0].value];
  }

  if (!validateEmptyValues(param.benchmarks, param.name)) {
    return '';
  }

  return [param.benchmarks[0]];
};

export const calculateDefaultParameters = (
  params: ParameterDescription[]
): Record<string, string | string[] | AssetLabelInput[]> => {
  return Object.fromEntries(params.map((param) => [param.name, calculateParameterDefaultValue(param)]));
};

export const createMetricRequestParameters = (
  values: Record<string, string | string[] | AssetLabelInput[]>,
  params: ParameterDescription[]
): IMetricCalculatorParameterInput[] => {
  const input: IMetricCalculatorParameterInput[] = [];
  for (const param of params) {
    const passParam =
      isNil(param.visibleIf) ||
      param.visibleIf.every((visibleIf) => {
        const value = values[visibleIf.parameter] as string[] | undefined;
        if (isNil(value)) {
          return true;
        }

        return value.includes(visibleIf.selectedOption);
      });

    if (!passParam) {
      continue;
    }

    const value = values[param.name];
    if (param.type === IParameterType.Int) {
      input.push({
        name: param.name,
        intValues: (value as string).split(',').map((val) => Number(val)),
      });
    } else if (param.type === IParameterType.Options) {
      const availableValues = new Set((param.options ?? []).map((opt) => opt.value));
      const selectedAvailableOptions = (value as string[]).filter((val) => availableValues.has(val));
      // when previous metrics had options realized, expected and someone selected only expected, then
      // after switching to a new metric with only realized option, there is no value selected. In this case,
      // we want to send a request for only realized option and later when someone switches to a different metric we want
      // to use expected
      if (availableValues.size === 1 && selectedAvailableOptions.length === 0) {
        selectedAvailableOptions.push(...availableValues.values());
      }

      input.push({
        name: param.name,
        strValues: selectedAvailableOptions,
      });
    } else {
      const value = values[param.name] as AssetLabelInput[];
      input.push({
        name: param.name,
        strValues: value.map((benchmark) => benchmark.id) ?? [],
      });
    }
  }

  return input;
};

export const formatMetricParameters = (
  params: {
    asset?: { symbol: string } | null;
    intValue?: number | null;
    name: string;
    strValue?: string | null;
  }[],
  paramDescription: {
    name: string;
    type: IParameterType;
    options?:
      | {
          id: string;
          value: string;
          label: string;
        }[]
      | null
      | undefined;
  }[]
): string => {
  const nameToDescription: Record<string, ParameterDescription> = Object.fromEntries(
    paramDescription.map((param) => [param.name, param])
  );
  return params
    .map((param) => {
      const description = nameToDescription[param.name];
      const paramType = description.type;
      if (paramType === IParameterType.Int) {
        return param.intValue?.toFixed(0) ?? '';
      }

      if (paramType === IParameterType.Options) {
        return (description.options ?? []).find((desc) => desc.value === param.strValue)?.label ?? '';
      }
      return param.asset?.symbol ?? '?';
    })
    .join(' - ');
};

export const calculateLabelToMetricParams = (
  metricParameters: IRiskDashboardInputQuery['portfolio']['metricParameters']
): Record<string, ParameterDescription[]> => {
  return Object.fromEntries(
    metricParameters.map((metric) => [
      metric.label,
      metric.parameters.map(
        (param): ParameterDescription => ({
          ...param,
          benchmarks: (param.benchmarks ?? []).filter((bench): bench is AssetLabelInput => isAssetLabelInput(bench)),
        })
      ),
    ])
  );
};

export const calculateAvailableContributionMetrics = (
  data?: IPortfolioMetricsQuery,
  date?: UtcDate
): AvailableContributionMetrics | null => {
  if (!data) {
    return null;
  }

  const contributionMetrics = data.portfolio.metrics
    .map((met) => {
      const seriesWithContrib = met.series.filter((series) => series.hasContributions);
      return {
        label: met.label,
        series: seriesWithContrib,
      };
    })
    .filter((met) => met.series.length > 0);

  const metrics = Object.fromEntries(
    contributionMetrics.map((met): [string, AvailableContributionMetrics['metrics'][string]] => [
      met.label,
      met.series.map((series) => series.parameters),
    ])
  );

  const dayJsDate = dayjs.max(
    contributionMetrics
      .flatMap((met) => met.series.flatMap((ser) => ser.values.flatMap((val) => val.date)))
      .map((date) => dayjs.utc(date.toString()))
  );

  return isNil(dayJsDate)
    ? null
    : {
        metrics,
        date: date ? date : convertDateInUtcToUTCISODate(dayJsDate),
        portfolioDefinition: uniq(
          data.portfolio.metrics
            .map((met) => met.portfolioDefinition)
            .filter((val): val is { name: string; id: string } => !isNil(val))
        ),
      };
};

export const calculatePorfolioMetricsQueryInput = ({
  metricInput,
  metricParamDescriptions,
  filters,
}: {
  metricInput: MetricInput;
  metricParamDescriptions: ParameterDescription[];
  filters: Pick<IPortfolioRiskMetricsInput, 'portfolioDefinitionIds' | 'subAccountAssetFilter' | 'since' | 'to'>;
}): {
  skip: boolean;
  input: IPortfolioMetricsQueryVariables['input'];
} => {
  const isValid =
    metricParamDescriptions.length > 0 && !hasValidationErrors(metricInput.params, metricParamDescriptions);

  return {
    skip: !isValid,
    input: {
      ...filters,
      metrics: [
        {
          label: metricInput.metric,
          parameters: !isValid ? [] : createMetricRequestParameters(metricInput.params, metricParamDescriptions),
        },
      ],
    },
  };
};

const warmUpPeriodEnableThreshold = 60;

export function metricHasExpandingWindow(metricSeries: MetricsData[number]['series'][number]): boolean {
  return metricSeries.parameters.find((p) => p.name === 'Window type')?.strValue === 'expanding';
}

export function isWarmUpPeriodParamEnabled(
  metricInput: MetricInput,
  metricParamDescriptions: ParameterDescription[]
): boolean {
  const windowTypeParam = metricParamDescriptions.find((p) => p.name === 'Window type');
  if (isNil(windowTypeParam)) {
    return false;
  }

  // param description supports expanding
  if (windowTypeParam.options?.every((o) => o.value !== 'expanding')) {
    return false;
  }

  const windowType = metricInput.params['Window type'];
  if (isNil(windowType)) {
    return false;
  }

  if (typeof windowType === 'string' || !Array.isArray(windowType)) {
    return false;
  }

  return windowType.some((val) => val === 'expanding');
}

export function isWarmUpPeriodEnabled(metricsData: MetricsData | undefined): boolean {
  const metricSeries = metricsData?.flatMap((met) => met.series) ?? [];
  // only expanding metrics have extreme values in the beginning
  const metricSeriesWithExpandingWindow = metricSeries.find(metricHasExpandingWindow);

  return (
    !isNil(metricSeriesWithExpandingWindow) &&
    metricSeriesWithExpandingWindow.values.length > warmUpPeriodEnableThreshold
  );
}

export function filterByWarmUpPeriod(
  warmUpPeriod: string,
  warmUpPeriodEnabled: boolean,
  metricsData: MetricsData | undefined
): MetricsData | undefined {
  const parsedWarmUpPeriod = Number.parseFloat(warmUpPeriod);
  const verifiedWarmUpPeriod = warmUpPeriodEnabled && Number.isSafeInteger(parsedWarmUpPeriod) ? parsedWarmUpPeriod : 0;

  return metricsData
    ? metricsData.map((met) => ({
        ...met,
        series: met.series.map((ser) => {
          if (metricHasExpandingWindow(ser)) {
            return {
              ...ser,
              values: ser.values.slice(verifiedWarmUpPeriod),
            };
          }
          return ser;
        }),
      }))
    : undefined;
}
